/** 剥离模型输出中残留的 XML tool call 块，避免在气泡里显示原始标签 */
export function stripXmlToolCallsDisplay(text) {
    if (!text)
        return text;
    const lower = text.toLowerCase();
    if (!lower.includes('function_calls') && !lower.includes('dsml'))
        return text;
    let s = text;
    s = s.replace(/<(?:\s*｜\s*DSML\s*[｜|]\s*)?function_calls>[\s\S]*?<\/(?:\s*｜\s*DSML\s*[｜|]\s*)?function_calls>/gi, '');
    s = s.replace(/<(?:\s*｜\s*DSML\s*[｜|]\s*)?function_calls(?:>|$)[\s\S]*$/i, '');
    s = s.replace(/<\s*｜\s*DSML\s*[｜|]\s*/gi, '');
    return s.trim();
}
/** HTML 实体解码（hermes-webui renderMd 前置步骤） */
export function decodeHtmlEntities(text) {
    return text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
}
/** 助手消息末尾 SEND_HTML:"/path/to/file.html" 标记（Bridge 会读取文件并下发预览事件） */
const SEND_HTML_MARKER_PATTERN = /\n?SEND_HTML:"[^"]+"\s*$/;
export function stripSendHtmlMarker(text) {
    if (!text)
        return text;
    return text.replace(SEND_HTML_MARKER_PATTERN, '').replace(/\s+$/, '');
}
/** 助手回复展示前清洗：去 XML tool 块 + SEND_HTML 标记 + HTML 实体解码 */
export function sanitizeAssistantMarkdownText(text) {
    return decodeHtmlEntities(stripSendHtmlMarker(stripXmlToolCallsDisplay(text || '')));
}
/**
 * 从 tool call 的 arguments JSON 中提取「子工具名」。
 *
 * MCP 代理类工具（如 mcp_tapd_proxy_execute_tool）本身只是一个转发壳，
 * 真正被调用的子工具名通常放在 arguments 的 name / tool_name / tool 等字段里。
 * 这里把这样的子工具名提取出来，供卡片头部以浅色二级名称展示。
 *
 * 后端 Hermes 流目前只在 arguments 里塞 {"label": ...}，没有子工具名时返回 undefined，
 * 前端会自动隐藏二级名称；CoPaw 流 / 历史消息回放则携带真实 arguments，可正常提取。
 */
export function getToolCallSubName(toolCall) {
    if (!toolCall.arguments)
        return undefined;
    try {
        const parsed = JSON.parse(toolCall.arguments);
        if (!parsed || typeof parsed !== 'object')
            return undefined;
        const candidates = ['name', 'tool_name', 'tool', 'function_name', 'function'];
        for (const key of candidates) {
            const v = parsed[key];
            if (typeof v === 'string' && v.trim()) {
                const sub = v.trim();
                // 与一级 MCP 名相同则视为无价值，不重复展示
                if (sub !== toolCall.name)
                    return sub;
            }
        }
    }
    catch {
        // ignore
    }
    return undefined;
}
function toolIdentity(toolCall) {
    if (toolCall.callId)
        return `id:${toolCall.callId}`;
    return `name:${toolCall.name || 'tool'}`;
}
/** 从末尾查找可更新的 pending tool call（按 callId 优先，否则同名且未完成） */
export function findPendingToolCallIndex(list, callId, name) {
    for (let i = list.length - 1; i >= 0; i--) {
        const tc = list[i].toolCall;
        if (!tc)
            continue;
        if (callId && tc.callId === callId)
            return i;
        if (name && tc.name === name && !tc.completed)
            return i;
    }
    return -1;
}
/** 合并/更新 tool call，避免 hermes.tool.progress 重复 START 产生大量卡片 */
export function upsertToolCallItem(list, info) {
    const idx = findPendingToolCallIndex(list, info.callId, info.name);
    if (idx >= 0) {
        const updated = [...list];
        const existing = updated[idx].toolCall;
        updated[idx] = {
            ...updated[idx],
            toolCall: {
                ...existing,
                name: info.name || existing.name,
                callId: info.callId || existing.callId,
                arguments: info.arguments ?? existing.arguments,
                output: info.output ?? existing.output,
                completed: info.completed ?? existing.completed,
            },
        };
        return updated;
    }
    return [...list, { isBotNotUser: true, text: '', toolCall: info }];
}
/**
 * 把指定 callId 的 tool call 标记为已完成（即使没有 output 也标记）。
 *
 * 后端两条 bridge 都可能发不带 output 的 TOOL_CALL_END：
 *   - Hermes：label == tool_name 时 output 为 None
 *   - CoPaw：output 为空字符串时不发 TOOL_CALL_END
 * 仅靠 output 是否存在判断 "已完成" 会让卡片一直停在运行中，所以需要一个独立的 completed 标记。
 */
export function markToolCallCompleted(list, callId, output) {
    if (!callId)
        return list;
    let changed = false;
    const updated = list.map((item) => {
        if (item.toolCall?.callId === callId) {
            changed = true;
            return {
                ...item,
                toolCall: {
                    ...item.toolCall,
                    completed: true,
                    output: output ?? item.toolCall.output,
                },
            };
        }
        return item;
    });
    return changed ? updated : list;
}
/**
 * RUN_FINISHED 兜底：把所有仍处于运行中的 tool call 标记为已完成。
 *
 * 防止后端因为异常 / 协议限制漏发 TOOL_CALL_END 导致卡片永远卡在 "运行中"。
 */
export function markAllToolCallsCompleted(list) {
    let changed = false;
    const updated = list.map((item) => {
        if (item.toolCall && !item.toolCall.completed) {
            changed = true;
            return {
                ...item,
                toolCall: { ...item.toolCall, completed: true },
            };
        }
        return item;
    });
    return changed ? updated : list;
}
/** 追加 HTML 预览卡片（由 Bridge CRASHCLAW_HTML_PREVIEW 事件触发） */
export function upsertHtmlPreviewItem(list, info) {
    const previewId = info.previewId;
    if (previewId) {
        const idx = list.findIndex((item) => item.htmlPreview?.previewId === previewId);
        if (idx >= 0) {
            const updated = [...list];
            updated[idx] = {
                ...updated[idx],
                htmlPreview: { ...updated[idx].htmlPreview, ...info },
            };
            return updated;
        }
    }
    return [...list, { isBotNotUser: true, text: '', htmlPreview: info }];
}
/** 追加文件交付卡片（由 Bridge CRASHCLAW_FILE_DELIVERY 事件触发） */
export function upsertFileDeliveryItem(list, info) {
    const deliveryId = info.deliveryId;
    if (deliveryId) {
        const idx = list.findIndex((item) => item.fileDelivery?.deliveryId === deliveryId);
        if (idx >= 0) {
            const updated = [...list];
            updated[idx] = {
                ...updated[idx],
                fileDelivery: { ...updated[idx].fileDelivery, ...info },
            };
            return updated;
        }
    }
    return [...list, { isBotNotUser: true, text: '', fileDelivery: info }];
}
/** 历史消息加载后按 callId / name 去重 tool call 卡片 */
export function dedupeToolCallItems(items) {
    const seen = new Set();
    const result = [];
    for (const item of items) {
        if (!item.toolCall) {
            result.push(item);
            continue;
        }
        const key = toolIdentity(item.toolCall);
        if (seen.has(key))
            continue;
        seen.add(key);
        result.push(item);
    }
    return result;
}
